/* eslint-disable yi/avoid-relative-parent-imports */
import type { CouponDetails } from '../coupons';
import type { OnSaleCourseDetails } from './OnSaleCourseDetails';

function isCouponCodeOverriden(coupon: CouponDetails | null): coupon is CouponDetails {
  return coupon !== null;
}

export class CourseSale {
  private details: OnSaleCourseDetails;
  private overridenCoupon: CouponDetails | null = null;
  private availableCoupon: CouponDetails[] = [];
  private hasCourseSaleBegun = false;

  constructor(courseDetails: OnSaleCourseDetails) {
    this.details = courseDetails;
  }

  public start() {
    this.hasCourseSaleBegun = true;
  }

  public stop() {
    this.hasCourseSaleBegun = false;
  }

  public saleOffer(): CouponDetails | null {
    if (this.hasCourseSaleBegun) {
      return this.details.sale ?? null;
    }
    return null;
  }

  public isOnSale(): boolean {
    const onSaleDetails = this.saleOffer();

    if (onSaleDetails === null) {
      return false;
    }
    const amount = onSaleDetails.value?.amount ?? 0;

    if (amount > 0) {
      return true;
    }

    return false;
  }

  public fullPrice() {
    return this.details.a_la_carte_price || 0;
  }

  private calculateDiscountedPrice(coupon: CouponDetails | null): number {
    const fullPrice = this.fullPrice();
    if (coupon === null) {
      return fullPrice;
    }

    if (coupon.value === null) {
      return fullPrice;
    }

    const { amount, type: valueType } = coupon.value;
    if (amount <= 0) {
      return fullPrice;
    }
    if (valueType === 'AMOUNT') {
      return fullPrice - amount;
    }
    if (valueType === 'PERCENTAGE') {
      return fullPrice * (1 - amount / 100);
    }
    return fullPrice;
  }

  private getBestSaleOffer() {
    const onSaleDetails = this.saleOffer();
    const allAvailableCoupons = [onSaleDetails, ...this.availableCoupon, this.overridenCoupon];

    const allCouponsWithValue = allAvailableCoupons.filter(item => item !== null);

    let bestPrice: null | number = null;
    let bestSaleOffer: null | CouponDetails = null;

    allCouponsWithValue.forEach(item => {
      if (bestPrice === null || bestSaleOffer === null) {
        bestPrice = this.calculateDiscountedPrice(item);
        bestSaleOffer = item;
      }
      const candidatePrice = this.calculateDiscountedPrice(item);
      if (candidatePrice < bestPrice) {
        bestPrice = candidatePrice;
        bestSaleOffer = item;
      }
    });

    return bestSaleOffer;
  }

  public bestOffer(): CouponDetails | null {
    if (isCouponCodeOverriden(this.overridenCoupon)) {
      return this.overridenCoupon;
    }

    const discountDetails = this.getBestSaleOffer();
    return discountDetails;
  }

  public addCoupon(couponDetails: CouponDetails | null) {
    if (couponDetails !== null) {
      this.availableCoupon.push(couponDetails);
    }
  }

  public chooseCoupon(coupon: CouponDetails): void {
    this.overridenCoupon = coupon;
  }
}
